/**
 * Kiosk templates list (Phase 4 T06). Route: /kiosks/templates
 * Design doc §"Admin-ui builder information architecture" items 1–2: list of
 * returned templates with draft/published/archived status, create form, and
 * row actions (open, duplicate, archive) wired to the routes in
 * `services/kiosks/README.md`. There is NO in-place edit of a published
 * version anywhere — the builder enforces immutability and this list only
 * links to it.
 */
import { useMemo, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { EmptyState } from '@/components/shared/EmptyState';
import { LoadingRows } from '@/components/shared/LoadingRows';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { kioskApiErrorText, formatKioskDate, KioskLoadError, KioskNavigation } from './KioskShared';
import {
  useArchiveKioskTemplateMutation,
  useCreateKioskTemplateMutation,
  useDuplicateKioskTemplateMutation,
  useGetKioskTemplatesQuery,
  type KioskTemplateRecord,
} from '@/store/api/kiosksApi';

export function KioskTemplatesPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [includeArchived, setIncludeArchived] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ name: '', storeId: '', workspaceId: '' });
  const [rowError, setRowError] = useState('');

  const page = useGetKioskTemplatesQuery({ includeArchived });
  const [createTemplate, createState] = useCreateKioskTemplateMutation();
  const [duplicateTemplate, duplicateState] = useDuplicateKioskTemplateMutation();
  const [archiveTemplate, archiveState] = useArchiveKioskTemplateMutation();

  const rows = useMemo(() => {
    const data = page.data ?? [];
    const term = search.trim().toLowerCase();
    return term ? data.filter((template) => template.name.toLowerCase().includes(term)) : data;
  }, [page.data, search]);

  async function submitCreate(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError('');
    try {
      const created = await createTemplate({
        name: form.name.trim(),
        ...(form.storeId.trim() ? { storeId: form.storeId.trim() } : {}),
        ...(form.workspaceId.trim() ? { workspaceId: form.workspaceId.trim() } : {}),
      }).unwrap();
      navigate(`/kiosks/templates/${encodeURIComponent(created.id)}`);
    } catch (caught) {
      setError(kioskApiErrorText(caught));
    }
  }

  async function duplicate(template: KioskTemplateRecord) {
    setRowError('');
    try {
      const result = await duplicateTemplate({ id: template.id }).unwrap();
      navigate(`/kiosks/templates/${encodeURIComponent(result.template.id)}`);
    } catch (caught) {
      setRowError(kioskApiErrorText(caught));
    }
  }

  async function archive(template: KioskTemplateRecord) {
    setRowError('');
    try {
      await archiveTemplate(template.id).unwrap();
    } catch (caught) {
      setRowError(kioskApiErrorText(caught));
    }
  }

  const busy = duplicateState.isLoading || archiveState.isLoading;

  return (
    <section className="space-y-4">
      <KioskNavigation active="templates" />
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">Kiosk templates</h1>
          <p className="text-sm text-muted-foreground">
            Versioned kiosk menu templates. Published versions are frozen; changes always start from a duplicate or a new draft.
          </p>
        </div>
        {!creating && <Button onClick={() => setCreating(true)}>Create template</Button>}
      </header>

      {creating && (
        <form className="max-w-xl space-y-4 rounded-md border p-4" onSubmit={(event) => void submitCreate(event)}>
          <h2 className="text-lg font-semibold">Create template</h2>
          <div className="space-y-2">
            <Label htmlFor="kiosk-template-name">Template name</Label>
            <Input
              id="kiosk-template-name"
              required
              maxLength={120}
              placeholder="Store 1 lunch menu"
              value={form.name}
              onChange={(event) => setForm({ ...form, name: event.target.value })}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="kiosk-template-store">Store ID (optional)</Label>
            <Input
              id="kiosk-template-store"
              value={form.storeId}
              onChange={(event) => setForm({ ...form, storeId: event.target.value })}
              aria-describedby="kiosk-template-store-help"
            />
            <span id="kiosk-template-store-help" className="text-xs text-muted-foreground">
              Use a returned store ID to scope the template. The service validates organization scope.
            </span>
          </div>
          <div className="space-y-2">
            <Label htmlFor="kiosk-template-workspace">Workspace ID (optional)</Label>
            <Input
              id="kiosk-template-workspace"
              value={form.workspaceId}
              onChange={(event) => setForm({ ...form, workspaceId: event.target.value })}
            />
          </div>
          {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" disabled={createState.isLoading} onClick={() => setCreating(false)}>Cancel</Button>
            <Button type="submit" disabled={!form.name.trim() || createState.isLoading}>
              {createState.isLoading ? 'Saving…' : 'Create template'}
            </Button>
          </div>
        </form>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <Input
          className="max-w-xs"
          aria-label="Search templates"
          placeholder="Search by name"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={includeArchived}
            onChange={(event) => setIncludeArchived(event.target.checked)}
          />
          Include archived
        </label>
        {search.trim() && rows.length === 0 && !page.isLoading && (
          <Button type="button" variant="ghost" onClick={() => setSearch('')}>Clear search</Button>
        )}
      </div>

      {rowError && <p role="alert" className="text-sm text-destructive">{rowError}</p>}

      {page.isLoading ? (
        <LoadingRows label="Loading kiosk templates" columns={5} />
      ) : page.isError ? (
        <KioskLoadError label="kiosk templates" retry={() => void page.refetch()} />
      ) : (page.data?.length ?? 0) === 0 ? (
        <EmptyState
          title="No kiosk templates yet."
          description="Create a template to start building a kiosk menu. Templates you create appear here."
          action={!creating ? <Button onClick={() => setCreating(true)}>Create template</Button> : undefined}
        />
      ) : rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">No templates match this search.</p>
      ) : (
        <div role="region" tabIndex={0} className="overflow-x-auto rounded-md border" aria-label="Kiosk templates">
          <table className="w-full min-w-[52rem] text-sm">
            <thead className="bg-muted/50 text-left">
              <tr>
                <th scope="col" className="px-4 py-3">Template</th>
                <th scope="col" className="px-4 py-3">Status</th>
                <th scope="col" className="px-4 py-3">Scope</th>
                <th scope="col" className="px-4 py-3">Updated</th>
                <th scope="col" className="px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((template) => (
                <tr key={template.id} className="border-t">
                  <td className="px-4 py-3">
                    <Link className="font-medium underline-offset-4 hover:underline" to={`/kiosks/templates/${encodeURIComponent(template.id)}`}>
                      {template.name}
                    </Link>
                    <span className="mt-1 block font-mono text-xs text-muted-foreground">{template.id}</span>
                  </td>
                  <td className="px-4 py-3">
                    {template.status === 'published'
                      ? <span>Published v{template.currentVersion} — frozen</span>
                      : template.status === 'archived'
                        ? <span>Archived</span>
                        : <span>Draft v{template.currentVersion} — editable</span>}
                  </td>
                  <td className="px-4 py-3">
                    <span className="block">Org: {template.orgId}</span>
                    {template.storeId && <span className="block">Store: {template.storeId}</span>}
                    {template.workspaceId && <span className="block">Workspace: {template.workspaceId}</span>}
                  </td>
                  <td className="px-4 py-3">{formatKioskDate(template.updatedAt)}</td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-2">
                      <Button asChild size="sm" variant="outline">
                        <Link to={`/kiosks/templates/${encodeURIComponent(template.id)}`}>Open</Link>
                      </Button>
                      <Button size="sm" variant="outline" disabled={busy || template.status === 'archived'} onClick={() => void duplicate(template)}>
                        Duplicate template
                      </Button>
                      <Button size="sm" variant="ghost" disabled={busy || template.status === 'archived'} onClick={() => void archive(template)}>
                        Archive
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
